/**
 * PHASE 8 — HUD DOM layer: compass tape, heading readout, tilt and
 * calibration-status hints. Renders the pure geometry from hud.ts.
 */

import { compassTape, deviceHeadingDeg, deviceTiltDeg, headingUsable } from "./hud";
import type { Quat } from "../orientation/quaternion";

export class HudLayer {
  private tapeEl: HTMLElement;
  private headingEl: HTMLElement;
  private nudgeEl: HTMLElement;
  private tapeKey = "";
  private lastHeading = NaN;
  private lastTilt = NaN;

  constructor(private container: HTMLElement) {
    this.tapeEl = container.querySelector<HTMLElement>(".tapetrack") ?? document.createElement("div");
    this.headingEl = container.querySelector<HTMLElement>(".heading") ?? document.createElement("div");
    this.nudgeEl = container.querySelector<HTMLElement>(".nudge") ?? document.createElement("div");
  }

  get root(): HTMLElement {
    return this.container;
  }

  /** Re-render from the camera quaternion; returns the current heading (or null). */
  sync(qCamera: Quat | null, widthPx: number): number | null {
    if (!qCamera) {
      this.container.style.opacity = "0.2";
      return null;
    }
    this.container.style.opacity = "1";
    const heading = deviceHeadingDeg(qCamera);
    const tilt = deviceTiltDeg(qCamera);
    const rounded = Math.round(heading);
    if (this.lastHeading !== rounded || this.lastTilt !== tilt) {
      this.headingEl.textContent = `${String(rounded).padStart(3, "0")}°`;
      this.headingEl.dataset.usable = headingUsable(tilt) ? "yes" : "no";
      this.container.classList.toggle("tilted", !headingUsable(tilt));
      this.lastHeading = rounded;
      this.lastTilt = tilt;
    }
    this.renderTape(heading, widthPx);
    return heading;
  }

  setNudge(text: string | null): void {
    if (text) {
      this.nudgeEl.textContent = text;
      this.nudgeEl.style.display = "block";
    } else {
      this.nudgeEl.style.display = "none";
    }
  }

  private renderTape(headingDeg: number, widthPx: number): void {
    // The tape only moves when the whole-degree heading changes; each tick
    // is at most a few pixels wide, so redrawing on the degree boundary is
    // visually seamless and avoids rebuilding ~17 DOM nodes per frame.
    const key = `${Math.round(headingDeg)}:${widthPx}`;
    if (key === this.tapeKey) return;
    this.tapeKey = key;
    const ticks = compassTape(headingDeg, widthPx - 24);
    this.tapeEl.replaceChildren();
    for (const tick of ticks) {
      const el = document.createElement("span");
      el.className = `tick${tick.major ? " major" : ""}`;
      el.style.left = `${widthPx / 2 + tick.offsetPx}px`;
      if (tick.label) el.textContent = tick.label;
      this.tapeEl.appendChild(el);
    }
  }
}